import React, { FC, useCallback, useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useGetScalingSize } from '@ui/Report';
import { useReportContext } from '@ui/Report/report.context';
import {
  REPORT_BODY_TEXT,
  REPORT_PAGE_HEADER_MAX_WIDTH,
} from './editable-page-header.constants';
import { resolveReportPrintBodyText } from '../../report-page-header.helpers';
import { ReportPageHeader } from './ReportPageHeader';
import { ReportPageTitleField } from './ReportPageTitleField';
import { ReportBodyTextField } from './ReportBodyTextField';

export type ReportEditableBodyFieldConfig = {
  id: string;
  text?: string;
  placeholder?: string;
  maxRows?: number;
  /**
   * When true, print/PDF shows {@link placeholder} if the user has not saved
   * body text. Prompt placeholders stay editor-only.
   */
  showPlaceholderInPrint?: boolean;
  onSave: (value: string) => void;
};

export type ReportEditablePageHeaderProps = {
  /** When this changes, local drafts reset from props (e.g. version or proposal id). */
  resetKey: string;
  /** Fallback title shown as placeholder when {@link titleText} is unset. */
  defaultTitle: string;
  titleText?: string;
  onSaveTitle: (value: string) => void;
  bodyFields?: ReportEditableBodyFieldConfig[];
  bodyLayout?: 'stack' | 'columns';
  /** Max width (cqw) for body fields in stack layout. */
  maxWidth?: number;
  /** When true, title spans the full width inside {@link ReportPageHeader} padding. */
  titleFullWidth?: boolean;
  /**
   * Max visual rows for body fields — fits the editable area on the report page.
   * Per-field {@link ReportEditableBodyFieldConfig.maxRows} overrides this.
   */
  bodyMaxRows?: number;
  /** Figma page-edge offsets. Off when {@link Report.Page} already applies padding. */
  edgeInset?: boolean;
  /** Small label after the title, e.g. `1/2` when a slide is split. */
  titleSuffix?: React.ReactNode;
};

/**
 * Editable report page header — title + optional body field(s).
 *
 * Title and body use placeholder for {@link defaultTitle} / field placeholders
 * when persisted text is empty. Print/export resolves the title to
 * `titleText.trim() || defaultTitle`. Body prompts are omitted in print unless
 * {@link ReportEditableBodyFieldConfig.showPlaceholderInPrint} is set.
 */
export const ReportEditablePageHeader: FC<ReportEditablePageHeaderProps> = ({
  resetKey,
  defaultTitle,
  titleText,
  onSaveTitle,
  bodyFields = [],
  bodyLayout = 'stack',
  maxWidth = REPORT_PAGE_HEADER_MAX_WIDTH,
  titleFullWidth = false,
  bodyMaxRows,
  edgeInset = true,
  titleSuffix,
}) => {
  const { isPrint } = useReportContext();
  const getScalingSize = useGetScalingSize();
  const { classes, cx } = useStyles({ getScalingSize });

  const [titleDraft, setTitleDraft] = useState(titleText ?? '');
  const [bodyDrafts, setBodyDrafts] = useState<Record<string, string>>(() =>
    toBodyDraftRecord(bodyFields),
  );

  useEffect(() => {
    setTitleDraft(titleText ?? '');
    setBodyDrafts(toBodyDraftRecord(bodyFields));
    // Reset drafts when switching pages or when persisted text changes externally.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed by resetKey + serialized body text
  }, [resetKey, titleText, serializeBodyTexts(bodyFields)]);

  const resolvedTitle = titleText?.trim() || defaultTitle;

  const handleSaveTitle = useCallback(() => {
    const next = titleDraft.trim();
    if (next === (titleText ?? '')) return;
    onSaveTitle(next);
  }, [onSaveTitle, titleDraft, titleText]);

  const handleSaveBody = useCallback(
    (field: ReportEditableBodyFieldConfig) => {
      const draft = bodyDrafts[field.id] ?? '';
      const next = draft.trim();
      if (next === (field.text ?? '')) return;
      field.onSave(next);
    },
    [bodyDrafts],
  );

  const titleNode = isPrint ? (
    resolvedTitle
  ) : (
    <ReportPageTitleField
      value={titleDraft}
      onChange={(e) => setTitleDraft(e.target.value)}
      onBlur={handleSaveTitle}
      placeholder={defaultTitle}
      maxWidth={titleFullWidth ? undefined : maxWidth}
      fitContent={titleSuffix != null}
    />
  );

  const bodyNode =
    bodyFields.length === 0 ? null : isPrint ? (
      <Box
        className={cx(
          classes.bodyLayout,
          bodyLayout === 'columns' && classes.bodyLayoutColumns,
        )}
      >
        {bodyFields.map((field) => {
          const text = resolveReportPrintBodyText(
            field.text,
            field.placeholder,
            { showPlaceholder: field.showPlaceholderInPrint === true },
          );
          if (!text) return null;
          return (
            <Box key={field.id} className={classes.bodyText}>
              {text}
            </Box>
          );
        })}
      </Box>
    ) : (
      <Box
        className={cx(
          classes.bodyLayout,
          bodyLayout === 'columns' && classes.bodyLayoutColumns,
        )}
      >
        {bodyFields.map((field) => {
          const resolvedMaxRows = field.maxRows ?? bodyMaxRows;
          return (
            <ReportBodyTextField
              key={field.id}
              value={bodyDrafts[field.id] ?? ''}
              onChange={(e) =>
                setBodyDrafts((prev) => ({
                  ...prev,
                  [field.id]: e.target.value,
                }))
              }
              onBlur={() => handleSaveBody(field)}
              placeholder={field.placeholder}
              maxRows={resolvedMaxRows}
              maxWidth={bodyLayout === 'columns' ? undefined : maxWidth}
            />
          );
        })}
      </Box>
    );

  return (
    <ReportPageHeader
      title={titleNode}
      edgeInset={edgeInset}
      titleSuffix={titleSuffix}
    >
      {bodyNode}
    </ReportPageHeader>
  );
};

const toBodyDraftRecord = (
  bodyFields: ReportEditableBodyFieldConfig[],
): Record<string, string> =>
  Object.fromEntries(bodyFields.map((field) => [field.id, field.text ?? '']));

const serializeBodyTexts = (
  bodyFields: ReportEditableBodyFieldConfig[],
): string =>
  bodyFields.map((field) => `${field.id}:${field.text ?? ''}`).join('|');

const useStyles = makeStyles<{
  getScalingSize: ReturnType<typeof useGetScalingSize>;
}>()((_theme, { getScalingSize }) => ({
  bodyLayout: {
    position: 'relative',
    zIndex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: getScalingSize(1),
    marginTop: getScalingSize(1),
    overflow: 'visible',
  },
  bodyLayoutColumns: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: getScalingSize(3),
    overflow: 'visible',
  },
  bodyText: {
    maxWidth: getScalingSize(REPORT_PAGE_HEADER_MAX_WIDTH),
    fontFamily: REPORT_BODY_TEXT.fontFamily,
    fontSize: getScalingSize(REPORT_BODY_TEXT.fontSize),
    fontWeight: REPORT_BODY_TEXT.fontWeight,
    lineHeight: REPORT_BODY_TEXT.lineHeight,
    color: REPORT_BODY_TEXT.color,
    textAlign: REPORT_BODY_TEXT.textAlign,
    // Match the editor textarea, which keeps line breaks. A plain block collapses them.
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
}));
